import { getAnimationDelay } from "@circle-react/components/Spaces/AiSummary/animations";
import { SpaceSummaryContent } from "@circle-react/components/Spaces/AiSummary/SummaryContent";
import { toLocaleString } from "@circle-react/helpers/number";
import type { Source } from "@circle-react/types/AiSummary/Source";
import { Drawer } from "@circleco/compass/components/Drawer";
import { SideDrawer } from "@circleco/compass/components/SideDrawer";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { startOfTomorrow, subDays, subMonths } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";
import { AiSummary } from "@/react/components/shared/AiSummary";
import { NoPostsStateV4 } from "@/react/components/Spaces/AiSummary/NoPostsStateV4";
import { getShortDateRange } from "@/react/helpers/dateTimeHelpers/getLocalizedRange";
import { SPACE_DATE_RANGE } from "@/react/types/AiSummary/SpaceSummary";
import type { Topic } from "@/react/types/AiSummary/Topic";

import { useSpaceSummary } from "../../hooks/useSpaceSummary";
import type { SpaceSummaryRailProps } from "./index";

export const SpaceSummaryRailV4 = ({
  isOpen,
  onClose,
  space,
  dateRange,
  type,
  firstUnreadMessageId,
}: SpaceSummaryRailProps) => {
  const dateFrom = useMemo(() => {
    if (dateRange === SPACE_DATE_RANGE.FROM_UNREAD) return undefined;
    const now = startOfTomorrow();
    return dateRange === SPACE_DATE_RANGE.LAST_7_DAYS
      ? subDays(now, 7).toISOString()
      : subMonths(now, 1).toISOString();
  }, [dateRange]);

  const {
    spaceSummary,
    isLoadingSpaceSummary,
    isNoDataError,
    contentCutOffMessage,
  } = useSpaceSummary({
    space,
    dateFrom: dateRange === SPACE_DATE_RANGE.FROM_UNREAD ? undefined : dateFrom,
    enabled: isOpen,
    type,
    firstUnreadMessageId:
      dateRange === SPACE_DATE_RANGE.FROM_UNREAD
        ? firstUnreadMessageId
        : undefined,
    dateRange,
  });

  const [animationKey, setAnimationKey] = useState(0);
  const formattedDateRange = dateFrom
    ? getShortDateRange(new Date(dateFrom), new Date())
    : t("ai_spaces_summaries.from_unread");
  const sourceName = space.name;

  useEffect(() => {
    setAnimationKey(prev => prev + 1);
  }, [dateRange]);

  const handleCollapseClick = (): void => {
    onClose();
  };

  const isMobile = useIsMobile();

  const { topics = [] } = spaceSummary || {};
  const allSources =
    topics
      .flatMap((topic: Topic) => topic.relevant_posts)
      .filter((source: unknown): source is Source => source !== undefined) ??
    [];

  const totalRecords = spaceSummary?.total_records;
  const isEmptyState = isNoDataError && !isLoadingSpaceSummary;

  const panelContent = (
    <div key={animationKey} className="flex flex-col gap-4">
      <AiSummary onCollapseClick={handleCollapseClick}>
        <SpaceSummaryContent
          spaceSummary={spaceSummary}
          sourceName={sourceName}
          formattedDateRange={formattedDateRange}
          isLoadingSpaceSummary={isLoadingSpaceSummary}
          allSources={allSources}
          topics={topics}
          contentCutOffMessage={contentCutOffMessage}
        />
        {typeof totalRecords === "number" && totalRecords > 0 && (
          <AiSummary.Footer>
            <div
              className="animate-fade-in-slow opacity-0"
              style={{
                animationDelay: getAnimationDelay(5, topics.length),
                animationFillMode: "forwards",
              }}
            >
              <AiSummary.FooterNote>
                {t(
                  `ai_spaces_summaries.${type === "chat" ? "messages" : "posts"}_summarized`,
                  {
                    count: totalRecords,
                    formatted_count: toLocaleString(totalRecords),
                  },
                )}
              </AiSummary.FooterNote>
            </div>
          </AiSummary.Footer>
        )}
      </AiSummary>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer.Root
        open={isOpen}
        onOpenChange={open => {
          if (!open) handleCollapseClick();
        }}
        snapTo="full"
      >
        <Drawer.Content aria-label={t("ai_spaces_summaries.panel_label")}>
          {isEmptyState ? (
            <div className="relative min-h-screen">
              <NoPostsStateV4 onCollapseClick={handleCollapseClick} />
            </div>
          ) : (
            <div className="-m-4 p-5">{panelContent}</div>
          )}
        </Drawer.Content>
      </Drawer.Root>
    );
  }

  // Viewport-fixed (no `container`) so the panel isn't clipped by the
  // AppShell's overflow-hidden / isolate stacking contexts. Matches the
  // Unified Inbox rail SideDrawer pattern.
  return (
    <SideDrawer.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleCollapseClick();
      }}
      noBackdrop
      position="right"
      size="sm"
    >
      <SideDrawer.Content aria-label={t("ai_spaces_summaries.panel_label")}>
        <SideDrawer.Body>
          {isEmptyState ? (
            <NoPostsStateV4 onCollapseClick={handleCollapseClick} />
          ) : (
            <div className="-m-4 p-5">{panelContent}</div>
          )}
        </SideDrawer.Body>
      </SideDrawer.Content>
    </SideDrawer.Root>
  );
};
